import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const nummerIcon = (label, kleur, klein = false) => L.divIcon({
  className: '',
  html: `<div style="width:${klein ? 22 : 28}px;height:${klein ? 22 : 28}px;border-radius:50%;background:${kleur};color:#fff;font:700 ${klein ? 10 : 12}px/1 Helvetica,Arial,sans-serif;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${label}</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const truckIcon = (naam, live) => L.divIcon({
  className: '',
  html: `<div style="position:relative;width:40px;height:40px;display:flex;align-items:center;justify-content:center">
    ${live ? '<div class="rk-pulse"></div>' : ''}
    <div style="width:34px;height:34px;border-radius:50%;background:${live ? '#0891B2' : '#6B7280'};border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
    </div>
    <div style="position:absolute;top:38px;left:50%;transform:translateX(-50%);white-space:nowrap;background:${live ? '#0891B2' : '#6B7280'};color:#fff;font:700 10px/1 Helvetica,Arial,sans-serif;padding:3px 6px;border-radius:2px">${(naam || 'Wagen').replace(/</g, '&lt;')}</div>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

const fmtTijd = (iso) => { try { return new Date(iso).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

function FitBounds({ punten }) {
  const map = useMap();
  useEffect(() => {
    if (!punten.length) return;
    if (punten.length === 1) { map.setView(punten[0], 13); return; }
    map.fitBounds(L.latLngBounds(punten), { padding: [30, 30], maxZoom: 14 });
  }, [map, JSON.stringify(punten)]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Meldt het huidige kaartbeeld (midden + zoom + afmetingen) zodat de PDF exact hetzelfde kan afdrukken
function ViewMelder({ onView }) {
  const map = useMap();
  useEffect(() => {
    if (!onView) return;
    const melden = () => {
      const c = map.getCenter(); const s = map.getSize();
      onView({ lat: +c.lat.toFixed(6), lon: +c.lng.toFixed(6), zoom: map.getZoom(), breedte: Math.round(s.x), hoogte: Math.round(s.y) });
    };
    map.on('moveend', melden);
    map.on('zoomend', melden);
    melden();
    return () => { map.off('moveend', melden); map.off('zoomend', melden); };
  }, [map, onView]);
  return null;
}

export const RouteKaart = ({ depot, stops = [], geometry, kleur = '#002FA7', hoogte = 560, voertuigen = [], spoor = [], onView }) => {
  const lijn = geometry?.coordinates ? geometry.coordinates.map(([lon, lat]) => [lat, lon]) : [];
  const punten = [
    ...(depot?.geo ? [[depot.geo.lat, depot.geo.lon]] : []),
    ...stops.filter(s => s.geo).map(s => [s.geo.lat, s.geo.lon]),
    ...voertuigen.filter(v => v.positie).map(v => [v.positie.lat, v.positie.lon]),
  ];
  return (
    <div className="border border-gray-200 bg-white relative" style={{ height: hoogte }} data-testid="route-kaart">
      <style>{`.rk-pulse{position:absolute;width:40px;height:40px;border-radius:50%;background:rgba(8,145,178,.45);animation:rkPulse 1.6s ease-out infinite}@keyframes rkPulse{0%{transform:scale(.6);opacity:.9}100%{transform:scale(1.8);opacity:0}}`}</style>
      <MapContainer center={[50.85, 4.35]} zoom={8} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
        <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
        {depot?.geo && (
          <Marker position={[depot.geo.lat, depot.geo.lon]} icon={nummerIcon('D', '#111827')}>
            <Popup><b>Vertrekpunt</b><br />{depot.tekst}</Popup>
          </Marker>
        )}
        {stops.filter(s => s.geo).map((s, i) => (
          <Marker key={s.doc_id} position={[s.geo.lat, s.geo.lon]} icon={nummerIcon(s.positie || i + 1, ['geleverd', 'bezocht'].includes(s.status) ? '#16A34A' : kleur, !s.positie)}>
            <Popup>
              <b>{s.klant_naam}</b><br />{s.adres?.tekst}<br />
              <span className="text-xs text-gray-500">{s.documentnr}{s.eta ? ` · ETA ${s.eta}` : ''}</span>
            </Popup>
          </Marker>
        ))}
        {lijn.length > 1 && <Polyline positions={lijn} pathOptions={{ color: kleur, weight: 4, opacity: 0.8 }} />}
        {spoor.length > 1 && <Polyline positions={spoor} pathOptions={{ color: '#0891B2', weight: 3, opacity: 0.9, dashArray: '6 6' }} />}
        {voertuigen.filter(v => v.positie).map(v => (
          <Marker key={v.rit_id} position={[v.positie.lat, v.positie.lon]} icon={truckIcon(v.chauffeur_naam, v.tracking_actief)} zIndexOffset={1000}>
            <Popup>
              <b>{v.chauffeur_naam || 'Wagen'}</b>{v.nummerplaat ? ` · ${v.nummerplaat}` : ''}<br />
              <span className="text-xs">{v.tracking_actief ? 'Live' : 'Laatste positie'} · {fmtTijd(v.positie.ts)}{v.positie.snelheid_kmh != null ? ` · ${Math.round(v.positie.snelheid_kmh)} km/u` : ''}</span><br />
              {v.volgende_stop ? <span className="text-xs">Volgende: #{v.volgende_stop.positie} {v.volgende_stop.klant_naam} (ETA {v.volgende_stop.eta})</span> : <span className="text-xs">Alle stops afgewerkt</span>}
            </Popup>
          </Marker>
        ))}
        <FitBounds punten={punten} />
        <ViewMelder onView={onView} />
      </MapContainer>
    </div>
  );
};

export default RouteKaart;
